"use client";

import {
    listAdminCoursePlansApiV1AdminCoursesCourseIdPlansGet,
    createAdminCoursePlanApiV1AdminCoursesCourseIdPlansPost,
    updateAdminCoursePlanApiV1AdminCoursesCourseIdPlansPlanIdPatch,
    deleteAdminCoursePlanApiV1AdminCoursesCourseIdPlansPlanIdDelete,
} from "@/src/api/sdk.gen";
import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { OrgPricingTiersSection } from "./OrgPricingTiersSection";

export type AdminCoursePlan = {
    id: number;
    name: string;
    description: string | null;
    price: number;
    currency: string | null;
    isActive: boolean;
};

export type AdminCourseLevelPlan = {
    levelId: number;
    levelName: string;
    levelNumber: number;
    plan: AdminCoursePlan | null;
};

type PlansPayload = {
    levels: AdminCourseLevelPlan[];
};

type LevelDraft = {
    price: string;
    currency: string;
    name: string;
    description: string;
};

const emptyDraft = (): LevelDraft => ({
    price: "25",
    currency: "USD",
    name: "",
    description: "",
});

function DraftFields({
    draft,
    setDraft,
}: {
    draft: LevelDraft;
    setDraft: (next: LevelDraft) => void;
}) {
    return (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-xs font-semibold text-slate-600">
                Price
                <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.price}
                    onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
            </label>
            <label className="block text-xs font-semibold text-slate-600">
                Currency
                <select
                    value={draft.currency}
                    onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                    <option value="USD">USD</option>
                    <option value="SAR">SAR</option>
                </select>
            </label>
            <label className="block text-xs font-semibold text-slate-600 sm:col-span-2">
                Display name
                <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
            </label>
            <label className="block text-xs font-semibold text-slate-600 sm:col-span-2">
                Description (optional)
                <input
                    type="text"
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
            </label>
        </div>
    );
}

export default function PlansTab({
    courseId,
    courseTitle,
    accessMode,
    onToast,
}: {
    courseId: number;
    courseTitle: string;
    accessMode: string;
    onToast: (message: string, type?: "success" | "error") => void;
}) {
    const [loading, setLoading] = useState(true);
    const [levels, setLevels] = useState<AdminCourseLevelPlan[]>([]);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [creatingLevelId, setCreatingLevelId] = useState<number | null>(null);
    const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
    const [createDraft, setCreateDraft] = useState<LevelDraft>(emptyDraft);
    const [editDraft, setEditDraft] = useState<LevelDraft>(emptyDraft);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error: reqErr } = await listAdminCoursePlansApiV1AdminCoursesCourseIdPlansGet({
                path: { course_id: courseId },
            });
            const json = (data || reqErr || {}) as PlansPayload & { error?: string };
            if (reqErr) {
                onToast(json.error || "Failed to load plans", "error");
                return;
            }
            setLevels(json.levels ?? []);
        } catch {
            onToast("Network error while loading plans", "error");
        } finally {
            setLoading(false);
        }
    }, [courseId, onToast]);

    useEffect(() => {
        void fetchPlans();
    }, [fetchPlans]);

    const isSponsored = accessMode === "SPONSORED";

    const startCreate = (level: AdminCourseLevelPlan) => {
        setCreatingLevelId(level.levelId);
        setEditingPlanId(null);
        setCreateDraft({
            ...emptyDraft(),
            name: `${courseTitle} · ${level.levelName} (Level ${level.levelNumber}) · Lifetime`,
        });
    };

    const startEdit = (plan: AdminCoursePlan) => {
        setEditingPlanId(plan.id);
        setCreatingLevelId(null);
        setEditDraft({
            price: String(plan.price),
            currency: plan.currency ?? "USD",
            name: plan.name,
            description: plan.description ?? "",
        });
    };

    const handleCreate = async (levelId: number) => {
        const price = Number.parseFloat(createDraft.price);
        if (!Number.isFinite(price) || price < 0) {
            onToast("Enter a valid price", "error");
            return;
        }
        setBusyKey(`create:${levelId}`);
        try {
            const { data, error: reqErr } = await createAdminCoursePlanApiV1AdminCoursesCourseIdPlansPost({
                path: { course_id: courseId },
                body: {
                    level_id: levelId,
                    price,
                    currency: createDraft.currency,
                    name: createDraft.name.trim() || undefined,
                    description: createDraft.description.trim() || null,
                },
            });
            const json = (data || reqErr || {}) as { error?: string };
            if (reqErr) {
                onToast(json.error || "Failed to create plan", "error");
                return;
            }
            onToast("Plan created");
            setCreatingLevelId(null);
            await fetchPlans();
        } catch {
            onToast("Network error while creating plan", "error");
        } finally {
            setBusyKey(null);
        }
    };

    const handleSaveEdit = async (planId: number) => {
        const price = Number.parseFloat(editDraft.price);
        if (!Number.isFinite(price) || price < 0) {
            onToast("Enter a valid price", "error");
            return;
        }
        setBusyKey(`edit:${planId}`);
        try {
            const { data, error: reqErr } = await updateAdminCoursePlanApiV1AdminCoursesCourseIdPlansPlanIdPatch({
                path: { course_id: courseId, plan_id: planId },
                body: {
                    price,
                    currency: editDraft.currency,
                    name: editDraft.name.trim(),
                    description: editDraft.description.trim() || null,
                },
            });
            const json = (data || reqErr || {}) as { error?: string };
            if (reqErr) {
                onToast(json.error || "Failed to update plan", "error");
                return;
            }
            onToast("Plan updated");
            setEditingPlanId(null);
            await fetchPlans();
        } catch {
            onToast("Network error while updating plan", "error");
        } finally {
            setBusyKey(null);
        }
    };

    const handleToggleActive = async (plan: AdminCoursePlan) => {
        setBusyKey(`active:${plan.id}`);
        try {
            const { data, error: reqErr } = await updateAdminCoursePlanApiV1AdminCoursesCourseIdPlansPlanIdPatch({
                path: { course_id: courseId, plan_id: plan.id },
                body: { is_active: !plan.isActive },
            });
            const json = (data || reqErr || {}) as { error?: string };
            if (reqErr) {
                onToast(json.error || "Failed to update plan", "error");
                return;
            }
            onToast(plan.isActive ? "Plan deactivated" : "Plan activated");
            await fetchPlans();
        } catch {
            onToast("Network error", "error");
        } finally {
            setBusyKey(null);
        }
    };

    const handleDelete = async (planId: number) => {
        if (!window.confirm("Remove this plan? If it has payments, it will be deactivated only.")) return;
        setBusyKey(`delete:${planId}`);
        try {
            const { data, error: reqErr } = await deleteAdminCoursePlanApiV1AdminCoursesCourseIdPlansPlanIdDelete({
                path: { course_id: courseId, plan_id: planId },
            });
            const json = (data || reqErr || {}) as { error?: string; message?: string };
            if (reqErr) {
                onToast(json.error || "Failed to remove plan", "error");
                return;
            }
            onToast(json.message || "Plan removed");
            setEditingPlanId(null);
            await fetchPlans();
        } catch {
            onToast("Network error", "error");
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/60 px-6 py-4">
                <h2 className="text-lg font-bold text-slate-900">Level pricing</h2>
                <p className="mt-1 text-sm text-slate-500">
                    One lifetime plan per level for individual-access courses. Students see{" "}
                    <span className="font-medium">Buy level</span> on the course page after placement.
                </p>
                {isSponsored && (
                    <p className="mt-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-900">
                        This course is <strong>sponsored</strong> — org members enroll without purchasing these
                        plans. Set access to Individual in Actions if you want paid levels.
                    </p>
                )}
            </div>

            <div className="p-6">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading plans…
                    </div>
                ) : levels.length === 0 ? (
                    <p className="text-sm text-slate-500">
                        No levels on this course yet. Add levels in the canonical draft, then return here to
                        set pricing.
                    </p>
                ) : (
                    <div className="space-y-4">
                        {levels.map((level) => {
                            const plan = level.plan;
                            const isCreating = creatingLevelId === level.levelId;
                            const isEditing = plan && editingPlanId === plan.id;

                            return (
                                <div
                                    key={level.levelId}
                                    className="rounded-xl border border-slate-200 p-4"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <p className="font-semibold text-slate-900">
                                                Level {level.levelNumber}: {level.levelName}
                                            </p>
                                            {plan ? (
                                                <p className="mt-1 text-sm text-slate-600">
                                                    {plan.name} — {plan.currency}{" "}
                                                    {plan.price.toFixed(2)}
                                                    {!plan.isActive && (
                                                        <span className="ml-2 font-semibold text-amber-700">
                                                            (inactive)
                                                        </span>
                                                    )}
                                                </p>
                                            ) : (
                                                <p className="mt-1 text-sm italic text-slate-500">
                                                    No plan — students will see Unavailable
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {plan ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => startEdit(plan)}
                                                        disabled={busyKey != null}
                                                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => void handleToggleActive(plan)}
                                                        disabled={busyKey != null}
                                                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                                    >
                                                        {busyKey === `active:${plan.id}`
                                                            ? "…"
                                                            : plan.isActive
                                                              ? "Deactivate"
                                                              : "Activate"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => void handleDelete(plan.id)}
                                                        disabled={busyKey != null}
                                                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                        Remove
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => startCreate(level)}
                                                    disabled={busyKey != null || isSponsored}
                                                    title={
                                                        isSponsored
                                                            ? "Not needed for sponsored courses"
                                                            : undefined
                                                    }
                                                    className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                    Add plan
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {isCreating && (
                                        <DraftFields draft={createDraft} setDraft={setCreateDraft} />
                                    )}
                                    {isCreating && (
                                        <div className="mt-3 flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => void handleCreate(level.levelId)}
                                                disabled={busyKey === `create:${level.levelId}`}
                                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                {busyKey === `create:${level.levelId}` ? "Saving…" : "Save plan"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCreatingLevelId(null)}
                                                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}

                                    {isEditing && plan && (
                                        <DraftFields draft={editDraft} setDraft={setEditDraft} />
                                    )}
                                    {isEditing && plan && (
                                        <div className="mt-3 flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => void handleSaveEdit(plan.id)}
                                                disabled={busyKey === `edit:${plan.id}`}
                                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                {busyKey === `edit:${plan.id}` ? "Saving…" : "Save changes"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditingPlanId(null)}
                                                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            </div>

            <OrgPricingTiersSection
                courseId={courseId}
                courseTitle={courseTitle}
                onToast={onToast}
            />
        </div>
    );
}
